import { useEffect, useState, useCallback } from "react";

/**
 * usePWAInstall — captures the `beforeinstallprompt` event so we can show
 * a custom "Install" button in the header. On iOS Safari there's no such
 * event, so we detect that separately and expose an `isIos` hint that
 * lets the caller show the "tap Share → Add to Home Screen" instructions.
 */
export function usePWAInstall() {
  const [promptEvent, setPromptEvent] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    // Detect iOS (Safari won't fire beforeinstallprompt)
    const ua = window.navigator.userAgent || "";
    setIsIos(/iPad|iPhone|iPod/.test(ua) && !window.MSStream);

    // Detect if already installed / running in standalone
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    const onPrompt = (e) => {
      e.preventDefault();
      setPromptEvent(e);
    };
    const onInstalled = () => {
      setPromptEvent(null);
      setIsStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!promptEvent) return { ok: false, reason: "no-prompt" };
    promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    setPromptEvent(null);
    return { ok: choice.outcome === "accepted", outcome: choice.outcome };
  }, [promptEvent]);

  return {
    canInstall: !!promptEvent,
    isStandalone,
    isIos,
    install,
  };
}
